/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useEffect,
  useCallback,
  useState,
  useMemo,
  useRef,
} from "react";
import MainLayout from "../../../layouts/MainLayout";
import { useNavigate, useLocation } from "react-router-dom";
import "../../../assets/styles/custom-styles.css";
import useNotification from "../../../hooks/useNotification";
import useForm from "../../../hooks/useForm";
import Form from "../../../components/forms/Form";
import { contractFields } from "../../../constants/forms/contractFields";
import {
  cleanPayload,
  convertStringsToFloats,
  getMissingRequiredFields,
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../utils/objectUtils";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import PageHeader from "../../../components/PageHeader";
import SectionHeader from "../../../components/forms/SectionHeader";
import SimpleBody from "../../../components/forms/SimpleBody";
import { v4 as uuidv4 } from "uuid";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useRemoteSelectOptions from "../../../hooks/useRemoteSelectOptions";
import useSelectOptions from "../../../hooks/useSelectOptions";
import ItemListSection from "../../../components/forms/ItemListSection";
import { transformFiltersForUrl } from "../../../utils/transformFiltersForUrl";
import useUrlFilters from "../../../hooks/useUrlFilters";
import { useSectionFields } from "../../../hooks/useSectionFields";
import { Box } from "@mui/material";
import { useOrgan } from "../../../hooks/useOrgan";

const CreateContractPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { updateField: updateProps, removeFields } = useSectionFields();

  const { selectedOrgan } = useOrgan();

  const { filters, pagination } = useUrlFilters(
    {
      id: [],
      number: [],
      name: [],
      "customer.name": [], // <--- Chave do filtro para relacionamento
      deleted_at: false,
    },
    "contract",
  );

  const { showNotification } = useNotification();
  const {
    post: baseServicePost,
    get: baseServiceGet,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);

  const [contractSection, itemsSection, jobsSection] = contractFields;

  const { formData, handleChange, setFormData, resetForm } = useForm(
    setDefaultFieldValues([contractSection]),
    [contractSection],
  );
  const [contractSectionState, setContractSectionState] = useState(
    contractSection.fields,
  );
  const {
    formData: itemFormData,
    handleChange: handleItemChange,
    setFormData: setItemFormData,
  } = useForm(setDefaultFieldValues([itemsSection]), [itemsSection]);
  const {
    formData: serviceFormData,
    handleChange: handleServiceChange,
    setFormData: setServiceFormData,
  } = useForm(setDefaultFieldValues([jobsSection]), [jobsSection]);

  const [items, setItems] = useState([]);
  const [jobs, setjobs] = useState([]);

  const handleItemsChange = useCallback(
    (newItems) => {
      setItems(newItems);
      setFormData((prev) => ({ ...prev, items: newItems }));
    },
    [setFormData],
  );

  const handlejobsChange = useCallback(
    (newjobs) => {
      setjobs(newjobs);
      setFormData((prev) => ({ ...prev, jobs: newjobs }));
    },
    [setFormData],
  );

  const contractTypeSelect = useRemoteSelectOptions({
    queryKey: ["contractTypes"],
    fetcher: async ({ page = 1, perPage = 15, search = "" }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        ...(search && { name: search }),
      });
      const params = {
        filters,
        page,
        perPage,
        sort: {
          column: "id",
          order: "asc",
        },
      };
      const response = await baseServiceGet(entities.contractTypes.get, params);
      return response.result;
    },
    fieldId: "contract.contract_type_id",
    formData,
    isMulti: false,
    mapToOption: (type) => ({ value: type.id, label: type.name }),
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
  });

  const contractStatusSelect = useRemoteSelectOptions({
    queryKey: ["contractStatus"],
    fetcher: async ({ page = 1, perPage = 15, search = "" }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        ...(search && { name: search }),
      });
      const params = {
        filters,
        page,
        perPage,
        sort: {
          column: "id",
          order: "asc",
        },
      };

      const response = await baseServiceGet(
        entities.contractStatus.get,
        params,
      );
      return response.result;
    },
    fieldId: "contract.contract_status_id",
    formData,
    isMulti: false,
    mapToOption: (type) => ({ value: type.id, label: type.name }),
    setFormData,
    enablePagination: true,
    initialPage: 1,
    perPage: 15,
  });

  const organizationSelect = useRemoteSelectOptions({
    queryKey: ["organizations"],
    fetcher: async ({ page = 1, perPage = 15, search = "" }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        ...(search && { name: search }), // busca por alias
      });
      const params = {
        filters,
        page,
        perPage,
        sort: {
          column: "id",
          order: "asc",
        },
      };

      const response = await baseServiceGet(entities.organizations.get, params);
      return response.result;
    },
    fieldId: "contract.organization_id",
    formData,
    isMulti: false,
    mapToOption: (type) => ({ value: type.id, label: type.name }),
    setFormData,
    enablePagination: true,
    initialPage: 1,
    perPage: 15,
  });

  const customerSelect = useRemoteSelectOptions({
    queryKey: ["customers"],
    fetcher: async ({ page = 1, perPage = 15, search = "" }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        alias: { $nin: ["ALUCOM", "MOREIA", "IP", "AS SISTEMAS"] },
        ...(search && { alias: search }), // busca por alias
      });
      const params = {
        filters,
        page,
        perPage,
        sort: {
          column: "id",
          order: "asc",
        },
      };

      const response = await baseServiceGet(entities.customers.get, params);
      return response.result;
    },
    fieldId: "contract.customer_id",
    formData,
    isMulti: false,
    mapToOption: (type) => ({
      value: type.id,
      label: `${type.id} - ${type.alias}`,
    }),
    setFormData,
    perPage: 15,
    enablePagination: true,
    refetchOnWindowFocus: true,
  });

  const isSameCustomerAndRecipientOptionsRaw = useMemo(
    () => [
      { value: true, label: "Sim" },
      { value: false, label: "Não" },
    ],
    [],
  );

  const isSameCustomerAndRecipientSelect = useSelectOptions({
    fieldId: "contract.is_same_customer_and_recipient",
    formData,
    rawOptions: isSameCustomerAndRecipientOptionsRaw,
    isMulti: false,
    mapToOption: (item) => ({ value: item.value, label: item.label }),
    setFormData,
  });

  const recipientCustomerSelect = useRemoteSelectOptions({
    queryKey: ["recipientCustomers", customerSelect.selectedValue?.value],
    fetcher: async ({ page = 1, perPage = 15, search = "" }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        alias: { $nin: ["ALUCOM", "MOREIA", "IP", "AS SISTEMAS"] },
        parent_id: customerSelect.selectedValue?.value,
        ...(search && { alias: search }),
      });
      const params = {
        filters,
        page,
        perPage,
        sort: {
          column: "id",
          order: "desc",
        },
      };
      const response = await baseServiceGet(entities.customers.get, params);
      return response.result;
    },
    fieldId: "contract.recipient_customer_ids",
    formData,
    isMulti: true,
    enabled: !!customerSelect.selectedValue?.value,
    mapToOption: (type) => ({
      value: type.id,
      label: `${type.id} - ${type.alias}`,
    }),
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    refetchOnWindowFocus: true,
  });

  // Atualizar a lista de clientes destinatários dinamicamente
  const prevCustomerRef = useRef(customerSelect.selectedValue?.value);

  useEffect(() => {
    const currentCustomer = customerSelect.selectedValue?.value;
    if (prevCustomerRef.current !== currentCustomer && currentCustomer) {
      recipientCustomerSelect.refetch();
      recipientCustomerSelect.reset(); // Limpa a seleção atual
      prevCustomerRef.current = currentCustomer;
    }
  }, [customerSelect.selectedValue?.value]);

  // Se o isSameCustomerAndRecipient for true, o recipientCustomerSelect deve ser desabilitado
  useEffect(() => {
    let updatedFields = [...contractSection.fields];

    const isSameCustomer =
      isSameCustomerAndRecipientSelect.selectedValue?.value;

    if (typeof isSameCustomer !== "undefined" && !isSameCustomer) {
      updatedFields = updateProps({
        arrayDependente: updatedFields,
        field_id: ["contract.recipient_customer_ids"],
        updates: { disabled: false },
      });
    }

    if (isSameCustomer) {
      handleChange(
        "contract.recipient_customer_ids",
        customerSelect.selectedValue?.value
          ? [customerSelect.selectedValue.value]
          : [],
      );
      updatedFields = removeFields({
        arrayDependente: updatedFields,
        field_id: ["contract.recipient_customer_ids"],
      });
    }

    setContractSectionState(updatedFields);
  }, [
    isSameCustomerAndRecipientSelect.selectedValue?.value,
    customerSelect.selectedValue?.value,
    contractSection.fields,
    updateProps,
    handleChange,
    removeFields,
    formData.contract.recipient_customer_ids,
  ]);

  const selects = useMemo(
    () => ({
      "contract.contract_type_id": contractTypeSelect,
      "contract.contract_status_id": contractStatusSelect,
      "contract.organization_id": organizationSelect,
      "contract.customer_id": customerSelect,
      "contract.is_same_customer_and_recipient":
        isSameCustomerAndRecipientSelect,
      "contract.recipient_customer_ids": recipientCustomerSelect,
    }),
    [
      contractTypeSelect,
      contractStatusSelect,
      organizationSelect,
      customerSelect,
      isSameCustomerAndRecipientSelect,
      recipientCustomerSelect,
    ],
  );

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      if (!selectConfig) return {};

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      };
    },
    [selects],
  );

  // Mutações TanStack Query
  const createContractMutation = useMutation({
    mutationFn: async (dataToCreate) => {
      setFormErrors({});
      const response = await baseServicePost(
        entities.contracts.create,
        dataToCreate,
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 201) {
        resetForm();
        navigate(`/aluguel/contratos`);
        queryClient.invalidateQueries({
          queryKey: ["contracts", filters, pagination],
        });
      }
    },
    onError: (err) => {
      console.log("onError ", err);
      if (err.response?.data?.errors) {
        setFormErrors(err.response.data.errors);
      }
    },
  });

  const handleBack = useCallback(() => {
    navigate(`/aluguel/contratos/`);
  }, [navigate]);

  // --- Funções para Selects (getOptions e getSelectedValue) ---
  const getOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      return selectConfig?.options || [];
    },
    [selects],
  );

  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      return selectConfig?.selectedValue || null;
    },
    [selects],
  );

  const handleSaveItem = useCallback(
    (itemToEdit) => {
      let updatedItems;
      const itemData = convertStringsToFloats(itemFormData.items);

      // Validar campos obrigatórios
      const missingFields = getMissingRequiredFields({
        itemData,
        fields: itemsSection.fields,
        prefix: "items",
        ignore: [],
      });

      if (missingFields.length > 0) {
        showNotification(
          "warning",
          "Todos os campos são obrigatórios para o item",
        );
        return false; // <-- erro -> NÃO fecha modal
      }

      if (itemToEdit) {
        updatedItems = items.map((item) =>
          item.id === itemToEdit.id ? { ...item, ...itemData } : item,
        );
      } else {
        updatedItems = [...items, { id: uuidv4().slice(0, 8), ...itemData }];
      }

      setItems(updatedItems);
      handleItemsChange(updatedItems);
      return true; // <-- sucesso -> pode fechar modal
    },
    [items, itemFormData, handleItemsChange],
  );

  const initializeItemForm = useCallback(
    (itemData) => {
      setItemFormData({ items: itemData || {} });
    },
    [setItemFormData],
  );

  const handleSaveService = useCallback(
    (serviceToEdit) => {
      let updatedjobs;
      const serviceData = serviceFormData.jobs;

      // Validar campos obrigatórios
      const missingFields = getMissingRequiredFields({
        itemData: serviceData,
        fields: jobsSection.fields,
        prefix: "jobs",
        ignore: [],
      });

      if (missingFields.length > 0) {
        showNotification(
          "warning",
          "Todos os campos são obrigatórios para o serviço",
        );
        return false; // <-- erro -> NÃO fecha modal
      }

      if (serviceToEdit) {
        updatedjobs = jobs.map((service) =>
          service.id === serviceToEdit.id
            ? { ...service, ...serviceData }
            : service,
        );
      } else {
        updatedjobs = [...jobs, { id: uuidv4().slice(0, 8), ...serviceData }];
      }

      setjobs(updatedjobs);
      handlejobsChange(updatedjobs);
      return true; // <-- sucesso -> pode fechar modal
    },
    [jobs, serviceFormData, handlejobsChange],
  );

  const initializeServiceForm = useCallback(
    (serviceData) => {
      setServiceFormData({ jobs: serviceData || {} });
    },
    [setServiceFormData],
  );

  const renderItemFormBody = useCallback(
    ({ fields, formData, handleFieldChange, formErrors }) => {
      return (
        <SimpleBody
          fields={fields}
          formErrors={normalizeFormErrors(formErrors)}
          formData={formData}
          handleFieldChange={handleFieldChange}
          getOptions={() => {}}
          getSelectedValue={() => {}}
        />
      );
    },
    [formErrors],
  );

  const renderServiceFormBody = useCallback(
    ({ fields, formData, handleFieldChange, formErrors }) => {
      return (
        <SimpleBody
          fields={fields}
          formErrors={normalizeFormErrors(formErrors)}
          formData={formData}
          handleFieldChange={handleFieldChange}
          getOptions={() => {}}
          getSelectedValue={() => {}}
        />
      );
    },
    [formErrors],
  );

  const getItemDisplayValue = useCallback((item, fieldId) => {
    const fieldName = fieldId.split(".").pop();
    const value = item[fieldName];

    if (value && typeof value === "object" && "label" in value) {
      return value.label;
    }

    return value || "";
  }, []);

  const getServiceDisplayValue = getItemDisplayValue;

  const handleSubmit = async () => {
    try {
      const { contract, info, items, jobs } = formData;
      const cleanedPayload = cleanPayload({ contract, info, items, jobs });

      if (items?.length < 1) {
        showNotification("warning", "Preencha pelo menos um item");
        return;
      }

      // TODO: remover temporariamente
      // if (jobs?.length < 1) {
      //   showNotification("warning", "Preencha pelo menos um serviço");
      //   return;
      // }

      const payload = {
        contract: {
          ...cleanedPayload.contract,
          number: String(cleanedPayload.contract.number),
          recipient_customer_ids: cleanedPayload.contract
            .is_same_customer_and_recipient
            ? [cleanedPayload.contract.customer_id]
            : cleanedPayload.contract.recipient_customer_ids?.map((recipient) =>
                Number(recipient.value),
              ),
          organization_id: selectedOrgan?.id,
        },
        info: cleanedPayload.info,
        items: cleanedPayload.items,
        jobs: cleanedPayload.jobs,
      };

      return await createContractMutation.mutateAsync(payload);
    } catch (error) {
      console.error("Erro ao enviar contrato:", {
        status: error.status,
        message: error.message,
      });
    }
  };
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Cadastro de Contrato"
        showBackButton={true}
        backUrl="/aluguel/contratos"
      />
      <div className="container-fluid p-1">
        <Form
          initialFormData={{ ...formData, items: items }}
          onSubmit={handleSubmit}
          textSubmit="Cadastrar"
          textLoadingSubmit="Cadastrando..."
          handleBack={handleBack}
          isLoading={false}>
          {() => (
            <>
              <div key={contractSection.section}>
                <SectionHeader section={contractSection.section} />
                <SimpleBody
                  fields={contractSectionState}
                  formErrors={normalizeFormErrors(formErrors)}
                  formData={formData}
                  handleFieldChange={handleChange}
                  getOptions={getOptions}
                  getSelectedValue={getSelectedValue}
                  getSelectMeta={getSelectMeta}
                />
              </div>
              <Box
                key={itemsSection.section}
                sx={{ mt: "1rem", display: "flex", flexDirection: "column" }}>
                <ItemListSection
                  section={itemsSection.section}
                  fields={itemsSection.fields}
                  initialData={items}
                  onItemsChange={handleItemsChange}
                  renderFormBody={renderItemFormBody}
                  getItemDisplayValue={getItemDisplayValue}
                  itemFormData={itemFormData}
                  onItemChange={handleItemChange}
                  itemFormErrors={formErrors}
                  onFormSubmit={handleSaveItem}
                  initializeItemForm={initializeItemForm}
                  isDisabledButton={false}
                />
              </Box>
              <Box
                key={jobsSection.section}
                sx={{ mt: "1rem", display: "flex", flexDirection: "column" }}>
                <ItemListSection
                  section={jobsSection.section}
                  fields={jobsSection.fields}
                  initialData={jobs}
                  onItemsChange={handlejobsChange}
                  renderFormBody={renderServiceFormBody}
                  getItemDisplayValue={getServiceDisplayValue}
                  itemFormData={serviceFormData}
                  onItemChange={handleServiceChange}
                  itemFormErrors={formErrors}
                  onFormSubmit={handleSaveService}
                  initializeItemForm={initializeServiceForm}
                  isDisabledButton={false}
                />
              </Box>
            </>
          )}
        </Form>
      </div>
    </MainLayout>
  );
};

export default CreateContractPage;
